import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import axios from 'axios';
import AppDownloadBadges from '../Common/AppDownloadBadges';

export default function Footer() {
    const location = useLocation();
    const isReportsPath = location.pathname.startsWith('/reports/');
    const [email, setEmail] = useState('');
    const [agreeToReceive, setAgreeToReceive] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [submitError, setSubmitError] = useState('');

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage('');
        setSubmitError('');

        try {
            const response = await axios.post('https://api.visist.ai/api/v1/newsletter/subscribe', {
                email,
                agreeToReceive
            });

            if (response.data.success) {
                setSubmitMessage('Thank you for subscribing to our newsletter!');
                setEmail('');
                setAgreeToReceive(false);
            } else {
                setSubmitError('Subscription failed. Please try again.');
            }
        } catch (error) {
            setSubmitError('An error occurred. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const currentYear = new Date().getFullYear();

    if (isReportsPath) {
        return null;
    }

    return (
        <footer className="w-full max-w-full overflow-x-hidden bg-emerald/90 text-white">
            <div className="container mx-auto w-full max-w-7xl px-6 py-16">
                {/* Main Footer Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
                    {/* Left Column - Logo & Description */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <img src="/logo192.png" alt="logo icon" className="h-12 w-12" />
                            <span className="text-2xl font-semibold">VISIST.AI</span>
                        </div>
                        <p className="text-gray-100 text-sm leading-relaxed max-w-sm font-medium">
                            Explore how VISIST.AI's technology solutions optimize sports performance and revolutionize the future of the sports industry.
                        </p>
                        {/* Social Media Icons */}
                        <div className="flex items-center gap-4">
                            <a
                                href="https://www.linkedin.com/company/visist-ai/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-8 h-8 flex items-center justify-center bg-black/20 rounded hover:bg-emerald transition-colors duration-300"
                            >
                                <i className="fa-brands fa-linkedin text-white"></i>
                            </a>
                            <a
                                href="https://www.instagram.com/visist.ai/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-8 h-8 flex items-center justify-center bg-black/20 rounded hover:bg-emerald transition-colors duration-300"
                            >
                                <i className="fa-brands fa-instagram text-white"></i>
                            </a>

                        </div>
                    </div>

                    {/* Navigation Links Column */}
                    <div>
                        <h3 className="text-lg font-bold mb-6">VISIST.AI</h3>
                        <ul className="space-y-3">
                            {[
                                { name: 'Products', path: '/products' },
                                { name: 'Solutions', path: '/solutions' },
                                { name: 'About Us', path: '/about' },
                                { name: 'News', path: '/blogs' },
                                { name: 'Join Our Team', path: '/careers' },
                                { name: 'Support', path: '/support' }
                            ].map((link, index) => (
                                <li key={index}>
                                    <NavLink
                                        to={link.path}
                                        onClick={scrollToTop}
                                        className={({ isActive }) => `${isActive ? 'text-black' : 'text-gray-100'} hover:text-white font-medium transition-colors duration-300`}
                                    >
                                        {link.name}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Mobile Apps Column */}
                    <div className="space-y-5">
                        <h3 className="text-lg font-bold">Mobile Apps</h3>
                        <div className="space-y-4">
                            <div>
                                <p className="text-xs font-semibold text-gray-100 uppercase tracking-wider mb-2">SmartAssessment</p>
                                <AppDownloadBadges appName="SmartAssessment" theme="footer" size="sm" layout="column" />
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-gray-100 uppercase tracking-wider mb-2">SmartJournal</p>
                                <AppDownloadBadges appName="SmartJournal" theme="footer" size="sm" layout="column" />
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Newsletter */}
                    <div>
                        <h3 className="text-lg font-bold mb-6">Subscribe to our newsletter:</h3>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="flex flex-col xl:flex-row gap-3">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    className="flex-1 px-4 py-3 bg-black/20 border border-white/20 rounded text-white placeholder-gray-200 focus:outline-none focus:border-white transition-colors duration-300 shadow-inner"
                                    required
                                />
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full xl:w-auto px-8 py-3 bg-[#339733] text-white font-semibold rounded-lg hover:bg-[#2d862d] transition-colors duration-300 shadow-lg border border-white/10 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                            {submitMessage && (
                                <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-100 text-sm font-medium">
                                    {submitMessage}
                                </div>
                            )}
                            {submitError && (
                                <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-100 text-sm font-medium">
                                    {submitError}
                                </div>
                            )}
                            <p className="text-xs text-gray-100 leading-relaxed font-medium">
                                VISIST.AI is dedicated to protecting your privacy and will only use your personal information to manage your account and provide the requested products and answers. If requested, VISIST.AI will contact you via phone or email.
                            </p>
                            <label className="flex items-start gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={agreeToReceive}
                                    onChange={(e) => setAgreeToReceive(e.target.checked)}
                                    className="mt-1 w-4 h-4 accent-deep-charcoal"
                                />
                                <span className="text-xs text-gray-100 font-medium">
                                    I agree to receive other communications from VISIST.AI.
                                </span>
                            </label>
                            <p className="text-xs text-gray-100 font-medium">
                                You can unsubscribe from these communications at any time. For details on understanding and our privacy practices, please review our{' '}
                                <Link to="/privacy_policy" onClick={scrollToTop} className="text-white underline hover:text-gray-200">
                                    Privacy Notice
                                </Link>
                                .
                            </p>
                        </form>
                    </div>
                </div>

                {/* Bottom Footer */}
                <div className="pt-8 border-t border-white/20">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-100 font-medium">
                        <p>© {currentYear} VISIST.AI. All rights reserved.</p>
                        <div className="flex items-center gap-6">
                            <Link
                                to="/privacy_policy"
                                onClick={scrollToTop}
                                className="hover:text-white transition-colors duration-300"
                            >
                                Privacy Notice
                            </Link>
                            <Link
                                to="/term_service"
                                onClick={scrollToTop}
                                className="hover:text-white transition-colors duration-300"
                            >
                                Terms &amp; Conditions
                            </Link>
                            <Link
                                to="/refund_policy"
                                onClick={scrollToTop}
                                className="hover:text-white transition-colors duration-300"
                            >
                                Refund Policy
                            </Link>
                        </div>
                    </div>
                </div>
            </div >
        </footer >
    );
}
